import axios, { type AxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.js';

export interface ApiEnvelope<T, M = unknown> {
  data: T;
  meta?: M;
}

/** In dev, default to same-origin so Vite proxies `/api` and WebSocket share one host. */
export const apiBaseUrl =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? '' : 'http://localhost:3001');

export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[API Error]', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      errors: error.response?.data?.errors,
      requestBody: error.config?.data,
    });

    if (error.response?.status === 401) {
      const url = String(error.config?.url ?? '');
      const errCode = (error.response?.data as { errors?: { code?: string }[] } | undefined)
        ?.errors?.[0]?.code;
      // PIN verification failures must never clear the login session.
      const isPinFailure =
        url.includes('/pin/verify') ||
        url.includes('/pin/verify-override') ||
        errCode === 'INVALID_PIN' ||
        errCode === 'PIN_LOCKED';
      if (!isPinFailure) {
        useAuthStore.getState().clearAuth();
        if (window.location.pathname !== '/login') {
          toast.error('Your session has expired. Please log in again.');
          setTimeout(() => {
            window.location.assign('/login');
          }, 2000);
        }
      }
    }
    return Promise.reject(error);
  },
);

export async function apiGet<T, M = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiEnvelope<T, M>> {
  const res = await apiClient.get<ApiEnvelope<T, M>>(url, config);
  return res.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<ApiEnvelope<T>> {
  const res = await apiClient.post<ApiEnvelope<T>>(url, body, config);
  return res.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<ApiEnvelope<T>> {
  const res = await apiClient.patch<ApiEnvelope<T>>(url, body, config);
  return res.data;
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiEnvelope<T>> {
  const res = await apiClient.delete<ApiEnvelope<T>>(url, config);
  return res.data;
}
